// Formatting helpers. Dates are shown as "2. 12. 2027" (Slovenian standard with spaces).

import type { AISystem, Light } from "../types";

function toDate(v: string | Date): Date | null {
  if (v instanceof Date) return v;
  if (!v) return null;
  // "2027-12-02" must be read as a local calendar date, not UTC midnight
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export function fmtDate(v?: string | Date | null): string {
  if (!v) return "";
  const d = toDate(v);
  if (!d) return String(v);
  // non-breaking spaces keep "2. 12. 2027" on one line
  return `${d.getDate()}.\u00a0${d.getMonth() + 1}.\u00a0${d.getFullYear()}`;
}

export function fmtDateTime(v?: string | Date | null): string {
  if (!v) return "";
  const d = toDate(v);
  if (!d) return String(v);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${fmtDate(d)} ob ${hh}.${mm}`;
}

export function daysBetween(from: Date, to: string | Date): number {
  const d = toDate(to);
  if (!d) return 0;
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((b - a) / 86400000);
}

export function parseDate(v: string): Date | null {
  return toDate(v);
}

export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function lightOf(s: Pick<AISystem, "triage">): Light | null {
  return s.triage?.light ?? null;
}

const LIGHT_ORDER: Record<string, number> = { red: 0, yellow: 1, green: 2, none: 3 };

export function lightRank(l: Light | null | undefined): number {
  return LIGHT_ORDER[l ?? "none"];
}

export function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
}

export function cleanDomain(v: string): string {
  return v
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "");
}

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  return window.location.origin + (pathOrUrl.startsWith("/") ? "" : "/") + pathOrUrl;
}
